import React, { useEffect, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../utils/formatDate';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const pad2 = (n) => String(n).padStart(2, '0');

const AdminReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [time, setTime] = useState('08:00');
  const [recipients, setRecipients] = useState(['']);
  const [lastSentAt, setLastSentAt] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [sendingTest, setSendingTest] = useState(false);
  const [testError, setTestError] = useState('');
  const [testSuccess, setTestSuccess] = useState('');

  useEffect(() => {
    fetch('/api/reports/schedule')
      .then((res) => res.json())
      .then((data) => {
        setEnabled(!!data.enabled);
        setFrequency(data.frequency || 'weekly');
        setDayOfWeek(data.dayOfWeek ?? 0);
        setDayOfMonth(data.dayOfMonth ?? 1);
        setTime(`${pad2(data.hour ?? 8)}:${pad2(data.minute ?? 0)}`);
        setRecipients(data.recipients && data.recipients.length > 0 ? data.recipients : ['']);
        setLastSentAt(data.lastSentAt || null);
      })
      .catch((err) => {
        console.error('❌ Error fetching report schedule:', err);
        setError('No se pudo cargar la configuración de reportes.');
      })
      .finally(() => setLoading(false));
  }, []);

  const updateRecipient = (index, value) => {
    setRecipients((list) => {
      const next = [...list];
      next[index] = value;
      return next;
    });
  };

  const addRecipientRow = () => setRecipients((list) => [...list, '']);

  const removeRecipientRow = (index) => {
    setRecipients((list) => (list.length === 1 ? [''] : list.filter((_, i) => i !== index)));
  };

  const handleSave = async () => {
    setError('');
    setSuccessMessage('');

    const cleanedRecipients = recipients.map((e) => e.trim()).filter(Boolean);
    const invalid = cleanedRecipients.find((e) => !EMAIL_RE.test(e));
    if (invalid) {
      setError(`"${invalid}" no es un correo electrónico válido.`);
      return;
    }

    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    setSaving(true);
    try {
      const res = await fetch('/api/reports/schedule', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled,
          frequency,
          dayOfWeek,
          dayOfMonth,
          hour,
          minute,
          recipients: cleanedRecipients,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al guardar la configuración.');

      setRecipients(data.recipients.length > 0 ? data.recipients : ['']);
      setLastSentAt(data.lastSentAt || null);
      setSuccessMessage('Configuración guardada.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setTestError('');
    setTestSuccess('');
    setSendingTest(true);
    try {
      const res = await fetch('/api/reports/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al enviar el reporte de prueba.');
      setTestSuccess('Reporte de prueba enviado.');
    } catch (err) {
      setTestError(err.message);
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-info" role="status" />
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Reportes</h2>
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin')}>
          Volver al Panel
        </Button>
      </div>
      <p className="text-muted">
        Programe el envío automático del reporte operativo mensual por correo. El sistema siempre
        envía el reporte del mes en curso, con los datos hasta el momento del envío.
      </p>

      <div className="card p-4">
        {error && <div className="alert alert-danger">{error}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="report-schedule-enabled"
            label="Envío automático activado"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        </Form.Group>

        <div className="row">
          <div className="col-md-4 mb-3">
            <Form.Label>Frecuencia</Form.Label>
            <Form.Select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
            </Form.Select>
          </div>

          {frequency === 'weekly' ? (
            <div className="col-md-4 mb-3">
              <Form.Label>Día</Form.Label>
              <Form.Select value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}>
                {DAYS_OF_WEEK.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </Form.Select>
            </div>
          ) : (
            <div className="col-md-4 mb-3">
              <Form.Label>Día del mes</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={28}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Math.min(28, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              />
            </div>
          )}

          <div className="col-md-4 mb-3">
            <Form.Label>Hora</Form.Label>
            <Form.Control type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
        </div>

        <Form.Label className="mt-2">Destinatarios</Form.Label>
        {recipients.map((email, index) => (
          <div key={index} className="d-flex mb-2" style={{ gap: '8px' }}>
            <Form.Control
              type="email"
              placeholder="correo@ejemplo.com"
              value={email}
              onChange={(e) => updateRecipient(index, e.target.value)}
            />
            <Button
              variant="outline-danger"
              onClick={() => removeRecipientRow(index)}
              title="Eliminar destinatario"
            >
              <i className="bi bi-trash3"></i>
            </Button>
          </div>
        ))}
        <Button variant="outline-secondary" size="sm" onClick={addRecipientRow} className="mb-3">
          + Agregar Destinatario
        </Button>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            {lastSentAt
              ? `Último envío: ${formatDateTime(lastSentAt)}`
              : 'Aún no se ha enviado ningún reporte automático.'}
          </small>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </div>
      </div>

      <div className="card p-4 mt-3">
        <h5>Enviar Prueba</h5>
        <p className="text-muted mb-2">
          Envía el reporte del mes en curso a los destinatarios guardados ahora mismo, sin esperar
          la próxima fecha programada — útil para confirmar que el correo llega correctamente.
        </p>
        {testError && <div className="alert alert-danger">{testError}</div>}
        {testSuccess && <div className="alert alert-success">{testSuccess}</div>}
        <Button variant="outline-primary" onClick={handleSendTest} disabled={sendingTest} style={{ width: 'fit-content' }}>
          {sendingTest ? 'Enviando...' : 'Enviar de Prueba Ahora'}
        </Button>
      </div>
    </div>
  );
};

export default AdminReports;
